import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import {
  CYCLES, CYCLE_LABELS, CYCLE_COLORS,
  EVAL_TYPES_BY_CYCLE, getBareme, getCycleFromCycleName,
  getTrimestreFromDate,
} from '../utils/cycle.helpers';

// Détecte le cycle depuis une classe (via son niveau)
function detectCycle(cls) {
  if (!cls) return null;
  const fromCycle = cls.level?.cycle?.name ? getCycleFromCycleName(cls.level.cycle.name) : null;
  return fromCycle;
}

function getTrimestreLabel(num) { return `Trimestre ${num}`; }

export default function EvaluationCreate() {
  const navigate = useNavigate();
  const [ref, setRef] = useState({ classes: [], subjects: [], teachers: [], periods: [], year: null });
  const [classSessions, setClassSessions] = useState([]);
  const [cycle, setCycle] = useState(null); // cycle de la classe sélectionnée
  const [form, setForm] = useState({
    type: 'COMPOSITION', classId: '', subjectId: '', teacherId: '', periodId: '',
    title: '', date: new Date().toISOString().slice(0, 10),
    baremeTotal: 20, coefficient: 1,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/catalog/classes'),
      api.get('/catalog/subjects'),
      api.get('/catalog/teachers'),
      api.get('/catalog/periods'),
      api.get('/catalog/active-year'),
    ]).then(([classes, subjects, teachers, periods, year]) => {
      setRef({ classes, subjects, teachers, periods, year });
      const firstClass = classes[0];
      const detectedCycle = detectCycle(firstClass);
      setCycle(detectedCycle);
      const types = EVAL_TYPES_BY_CYCLE[detectedCycle] || EVAL_TYPES_BY_CYCLE[CYCLES.POST_ELEMENTAIRE];
      const defaultType = types[0]?.v || 'COMPOSITION';
      const defaultBareme = types[0]?.bareme || 20;
      setForm((f) => ({
        ...f,
        classId: firstClass?.id || '',
        subjectId: subjects[0]?.id || '',
        teacherId: teachers[0]?.id || '',
        periodId: periods[0]?.id || '',
        type: defaultType,
        baremeTotal: defaultBareme,
      }));
      if (firstClass?.id) loadClassSessions(firstClass.id);
    }).catch((err) => setError(err.message));
  }, []);

  function loadClassSessions(classId) {
    api.get(`/timetable?classId=${classId}`).then((s) => setClassSessions(s || [])).catch(() => setClassSessions([]));
  }

  // Changer de classe → recalculer le cycle et les types d'éval
  function onClassChange(e) {
    const classId = e.target.value;
    const cls = ref.classes.find((c) => c.id === classId);
    const newCycle = detectCycle(cls);
    setCycle(newCycle);
    const types = EVAL_TYPES_BY_CYCLE[newCycle] || EVAL_TYPES_BY_CYCLE[CYCLES.POST_ELEMENTAIRE];
    const defaultType = types[0]?.v || 'COMPOSITION';
    const defaultBareme = getBareme(newCycle, defaultType) || types[0]?.bareme || 20;
    setForm((f) => ({ ...f, classId, type: defaultType, baremeTotal: defaultBareme, teacherId: '' }));
    setClassSessions([]);
    if (classId) loadClassSessions(classId);
  }

  // Changer de type → recalculer le barème automatiquement
  function onTypeChange(e) {
    const type = e.target.value;
    const autoBareme = getBareme(cycle, type);
    setForm((f) => ({ ...f, type, baremeTotal: autoBareme || f.baremeTotal }));
  }

  // Matières filtrées selon le cycle
  const filteredSubjects = useMemo(() => {
    if (!cycle) return ref.subjects;
    // Si des matières ont cycleCode, filtrer — sinon tout afficher
    const withCode = ref.subjects.filter((s) => s.cycleCode === cycle);
    return withCode.length > 0 ? withCode : ref.subjects;
  }, [ref.subjects, cycle]);

  // Enseignants filtrés selon classe + matière
  const filteredTeachers = useMemo(() => {
    if (!classSessions.length) return ref.teachers;
    if (!form.subjectId) {
      const ids = new Set(classSessions.map((s) => s.teacherId).filter(Boolean));
      return ref.teachers.filter((t) => ids.has(t.id));
    }
    const ids = new Set(classSessions.filter((s) => s.subjectId === form.subjectId).map((s) => s.teacherId).filter(Boolean));
    const filtered = ref.teachers.filter((t) => ids.has(t.id));
    return filtered.length > 0 ? filtered : ref.teachers;
  }, [classSessions, form.subjectId, ref.teachers]);

  function onSubjectChange(e) {
    const subjectId = e.target.value;
    const session = classSessions.find((s) => s.subjectId === subjectId);
    const teacherId = session?.teacherId || form.teacherId;
    setForm((f) => ({ ...f, subjectId, teacherId }));
  }

  // Titre automatique : Type + Matière
  const autoTitle = useMemo(() => {
    const evalTypes = EVAL_TYPES_BY_CYCLE[cycle] || EVAL_TYPES_BY_CYCLE[CYCLES.POST_ELEMENTAIRE];
    const typeLabel = evalTypes.find((t) => t.v === form.type)?.l || form.type;
    const subjectName = ref.subjects.find((s) => s.id === form.subjectId)?.name || '';
    return typeLabel && subjectName ? `${typeLabel} de ${subjectName}` : '';
  }, [form.type, form.subjectId, ref.subjects, cycle]);

  const autoTrimestre = useMemo(() => getTrimestreFromDate(form.date), [form.date]);

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    if (k === 'date') {} // autoTrimestre se recalcule via useMemo
  };

  // Types d'évaluation selon le cycle
  const evalTypes = EVAL_TYPES_BY_CYCLE[cycle] || EVAL_TYPES_BY_CYCLE[CYCLES.POST_ELEMENTAIRE];
  const cycleColor = CYCLE_COLORS[cycle] || 'var(--primary)';
  const cycleLabel = CYCLE_LABELS[cycle] || '';

  // Pré-Scolaire : rediriger vers le module dédié
  if (cycle === CYCLES.PRESCOLAIRE) {
    return (
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-head">
          <h2>Nouvelle évaluation</h2>
          <span className="badge" style={{ background: cycleColor + '20', color: cycleColor }}>{cycleLabel}</span>
        </div>
        <div className="card-body" style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🎨</div>
          <h3 style={{ marginBottom: 8 }}>Classe Pré-Scolaire détectée</h3>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 20 }}>
            Le Pré-Scolaire utilise un système d'observations par domaines d'activités (Acquis / En cours / Non Acquis) plutôt que des notes chiffrées.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button className="btn" onClick={() => navigate(`/evaluations/prescolaire/new?classId=${form.classId}`)}
              style={{ width: 'auto', padding: '0 24px' }}>
              🎨 Fiche d'observation Pré-Scolaire
            </button>
            <button className="btn-ghost" onClick={() => navigate('/evaluations')}>Annuler</button>
          </div>
        </div>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!ref.year) { setError('Aucune année scolaire active.'); return; }
    setSaving(true);
    try {
      // Résoudre le periodId
      let periodId = form.periodId;
      if (!periodId && ref.periods.length > 0) {
        const match = ref.periods.find((p) => p.name?.includes(String(autoTrimestre)) || p.order === autoTrimestre);
        periodId = match?.id || ref.periods[autoTrimestre - 1]?.id || ref.periods[0]?.id;
      }
      const created = await api.post('/evaluations', {
        academicYearId: ref.year.id,
        periodId,
        classId: form.classId,
        subjectId: form.subjectId,
        teacherId: form.teacherId,
        type: form.type,
        title: form.title || autoTitle,
        date: form.date,
        baremeTotal: Number(form.baremeTotal),
        coefficient: Number(form.coefficient),
      });
      navigate(`/evaluations/${created.id}/saisie`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 720 }}>
      <div className="card-head">
        <h2>Nouvelle évaluation</h2>
        {cycleLabel && (
          <span className="badge" style={{ background: cycleColor + '20', color: cycleColor }}>
            {cycleLabel}
          </span>
        )}
      </div>
      <div className="card-body" style={{ padding: 20 }}>
        {error && <div className="form-error">{error}</div>}

        {/* Infos cycle */}
        {cycle === CYCLES.ELEMENTAIRE && (
          <div style={{ padding: '10px 14px', background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 8, marginBottom: 14, fontSize: 12, color: '#1d4ed8' }}>
            📚 <b>Élémentaire</b> — Contrôle /10 · Composition /20 · Le barème s'adapte automatiquement au type.
          </div>
        )}

        <form onSubmit={submit} className="form-grid">
          <div className="two-col">
            <div className="field">
              <label>Classe</label>
              <select value={form.classId} onChange={onClassChange}>
                <option value="">—</option>
                {ref.classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Matière</label>
              <select value={form.subjectId} onChange={onSubjectChange}>
                <option value="">—</option>
                {filteredSubjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div className="two-col">
            <div className="field">
              <label>Type d'évaluation</label>
              <select value={form.type} onChange={onTypeChange}>
                {evalTypes.map((t) => (
                  <option key={t.v} value={t.v}>{t.l} (/{t.bareme})</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" value={form.date} onChange={set('date')} />
            </div>
          </div>

          <div className="field">
            <label>
              Titre
              {autoTitle && <span style={{ color: 'var(--muted)', fontSize: '0.75rem', marginLeft: 8 }}>Auto : "{autoTitle}"</span>}
            </label>
            <input value={form.title} onChange={set('title')} placeholder={autoTitle || "Titre de l'évaluation"} />
            {autoTitle && !form.title && (
              <small style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>Laisser vide pour utiliser le titre automatique</small>
            )}
          </div>

          <div className="two-col">
            <div className="field">
              <label>
                Enseignant
                {classSessions.length > 0 && form.subjectId && (
                  <span style={{ color: 'var(--muted)', fontSize: '0.72rem', marginLeft: 8 }}>(filtrés depuis EDT)</span>
                )}
              </label>
              <select value={form.teacherId} onChange={set('teacherId')}>
                <option value="">—</option>
                {filteredTeachers.map((t) => (
                  <option key={t.id} value={t.id}>{t.lastName} {t.firstName}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Trimestre <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(automatique)</small></label>
              <select value={form.periodId} onChange={set('periodId')}>
                <option value="">— Auto : {getTrimestreLabel(autoTrimestre)} —</option>
                {ref.periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <small style={{ color: 'var(--success, green)', fontSize: '0.72rem' }}>
                ✓ Détecté : {getTrimestreLabel(autoTrimestre)} (Oct–Jan · Fév–Avr · Mai–Juil)
              </small>
            </div>
          </div>

          <div className="two-col">
            <div className="field">
              <label>
                Barème
                {cycle === CYCLES.ELEMENTAIRE && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--primary)' }}>
                    (auto selon le type)
                  </span>
                )}
              </label>
              <select value={form.baremeTotal} onChange={set('baremeTotal')}>
                {[5, 10, 20, 40, 60, 100].map((b) => (
                  <option key={b} value={b}>/{b}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Coefficient</label>
              <input type="number" min="0.5" step="0.5" value={form.coefficient} onChange={set('coefficient')} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button className="btn" type="submit" disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
              {saving ? 'Création…' : 'Créer et saisir les notes'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => navigate('/evaluations')}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
